import React, { useState, useMemo } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Input, Avatar } from 'antd';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { UserDto, UserRole, UserStatus } from '@wellverse/shared-ts';

const roleColors: Record<UserRole, string> = {
  [UserRole.USER]: '#898989',
  [UserRole.MODERATOR]: '#3ecf8e',
  [UserRole.ADMIN]: '#f5a623',
  [UserRole.TRAINER]: '#1890ff',
};

const statusColors: Record<UserStatus, string> = {
  [UserStatus.ACTIVE]: '#3ecf8e',
  [UserStatus.INACTIVE]: '#898989',
  [UserStatus.PENDING]: '#f5a623',
  [UserStatus.SUSPENDED]: '#ff6b6b',
};

export const UserList: React.FC = () => {
  const [searchText, setSearchText] = useState('');

  // Searched on the server, so a user on any page can be found — filtering the
  // loaded page only ever looked through 20 rows.
  const permanentFilters = useMemo(
    () =>
      searchText
        ? [{ field: 'search', operator: 'contains' as const, value: searchText }]
        : [],
    [searchText],
  );

  const { tableProps } = useTable<UserDto>({
    resource: 'users',
    filters: { permanent: permanentFilters },
    pagination: {
      pageSize: 20,
    },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="users">
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search by name, email, phone, User ID, Friend ID, referral code"
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value.trim())}
          style={{ width: 460, maxWidth: '100%' }}
          allowClear
        />
      </div>
      <Table {...tableProps} rowKey="id">
        <Table.Column
          title="User"
          width={200}
          render={(_: unknown, record: UserDto) => (
            <Space>
              <Avatar
                src={record.avatar}
                icon={!record.avatar && <UserOutlined />}
                size={32}
              />
              <div>
                <div style={{ fontWeight: 500 }}>{record.fullName}</div>
                <div style={{ fontSize: 12, color: '#898989' }}>
                  {record.email || record.phone || '—'}
                </div>
              </div>
            </Space>
          )}
        />
        <Table.Column
          dataIndex="role"
          title="Role"
          width={120}
          filters={Object.values(UserRole).map((r) => ({ text: r, value: r }))}
          onFilter={(value, record: any) => record.role === value}
          render={(role: UserRole) => (
            <Tag
              style={{
                borderColor: roleColors[role],
                color: roleColors[role],
                background: `${roleColors[role]}15`,
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {role}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="status"
          title="Status"
          width={110}
          filters={Object.values(UserStatus).map((s) => ({ text: s, value: s }))}
          onFilter={(value, record: any) => record.status === value}
          render={(status: UserStatus) => (
            <Tag
              style={{
                borderColor: statusColors[status] || '#898989',
                color: statusColors[status] || '#898989',
                background: `${statusColors[status] || '#898989'}15`,
                borderRadius: 9999,
                padding: '2px 12px',
                fontWeight: 500,
              }}
            >
              {status}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="handle"
          title="User ID"
          width={140}
          render={(handle?: string) => (handle ? `@${handle}` : '—')}
        />
        <Table.Column
          dataIndex="friendCode"
          title="Friend ID"
          width={130}
          render={(code?: string) => code ?? '—'}
        />
        <Table.Column
          dataIndex="referralCode"
          title="Referral Code"
          width={150}
          render={(code?: string) => code ?? '—'}
        />
        <Table.Column
          dataIndex="createdAt"
          title="Created At"
          width={160}
          render={(date: string) =>
            date ? new Date(date).toLocaleString() : '—'
          }
        />
        <Table.Column
          title="Actions"
          width={120}
          dataIndex="actions"
          render={(_: unknown, record: UserDto) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} resource="users" />
              <ShowButton hideText size="small" recordItemId={record.id} resource="users" />
              <DeleteButton hideText size="small" recordItemId={record.id} resource="users" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
